import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { User } from '@app/auth/store/auth.interface';
import { selectUser } from '@app/auth/store/auth.selectors';
import { EditLanguagesService } from '@shared/services/edit-languages.service';
import { FroalaEditorService } from '@shared/services/froala-editor.service';

import FroalaEditor from 'froala-editor';

@Component({
    selector: 'app-froala-editor',
    templateUrl: './froala-editor.component.html',
    styleUrls: [
        './froala-editor.component.scss',
        '../../../../../node_modules/froala-editor/css/froala_editor.min.css',
        '../../../../../node_modules/froala-editor/css/froala_editor.pkgd.css',
        '../../../../../node_modules/froala-editor/css/plugins/code_view.min.css',
        '../../../../../node_modules/froala-editor/css/plugins/table.min.css',
        '../../../../../node_modules/froala-editor/css/froala_style.css',
        '../../../../../node_modules/froala-editor/css/plugins/image.min.css',
        '../../../../../node_modules/froala-editor/css/plugins/colors.min.css'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FroalaEditorComponent implements OnInit {
  validators = [];
  content = '';
  editor;
  canSave = false;
  error = '';
  isReplacement: boolean;
  user: User;
  autotranslate = false;

  public options = {
    events: {
      'initialized': (e) => {
        this.editor = e.getEditor();
      },
      'contentChanged': () => {
        const content = this.editor.html.get();
        const error = '';
        for (const validator of this.validators) {
          const result = validator(content);
          if ('error' in result) {
            this.error = result.error;
            this.canSave = false;
            this.cdr.detectChanges();
            return;
          }
        }
        this.error = '';
        this.canSave = true;
        this.cdr.detectChanges();
      },
    },
    attribution: false,
    autofocus: true,
    charCounterCount: true,
    fullPage: true,
    htmlExecuteScripts: false,
    htmlRemoveTags: ['script', 'base'],
    pluginsEnabled: [
      'align', 'charCounter', 'codeView', 'fontSize', 'lists', 'image', 'colors',
      'paragraphStyle', 'table', 'url', 'paragraphFormat', 'addPhishLinkPlugin', 'link', 'fontFamily'
    ],
    height: 'calc(80vh - 235px)',
    heightMax: 'calc(80vh - 290px)',
    toolbarButtons: [
      ['bold', 'italic', 'underline', 'textColor', 'clearFormatting', 'fontSize', 'fontFamily'],
      ['alignDropdown', 'formatOLSimple', 'formatUL', 'outdent', 'indent'],
      ['addPhishLink', 'insertImage', 'insertTable', 'insertFile', 'html', 'undo', 'redo', 'custom_tags_dropdown']
    ],
    imageInsertButtons: ['imageUpload', "|", 'imageByURL'],
    imageEditButtons: ['imageDisplay', 'imageAlign', 'imageInfo', 'imageRemove', 'imagePhishLink'],
    useClasses: false,
    imageUpload: true,
    imagePaste: false,
    editorClass: '',
    key: environment.froalaApiKey,
    zIndex: 9900,
    linkEditButtons: ['linkRemove'],
    requestWithCredentials: true,
    imageUploadParam: 'image',
    imageUploadURL: `${environment.api}` + '/attack-image-upload/',
    imageUploadParams: {
      cp: '',
    },
    imageUploadMethod: 'POST',
    imageMaxSize: 5 * 1024 * 1024,
    imageAllowedTypes: ['jpeg', 'jpg', 'png'],
    fontFamily: {
      "Roboto,sans-serif": 'Roboto',
      "Oswald,sans-serif": 'Oswald',
      "Montserrat,sans-serif": 'Montserrat',
      "'Open Sans Condensed',sans-serif": 'Open Sans Condensed',
      "Arial,Helvetica,sans-serif": 'Arial',
      "Georgia,serif": 'Georgia', 'Impact,Charcoal,sans-serif': 'Impact',
      "Tahoma,Geneva,sans-serif": 'Tahoma',
      "'Times New Roman',Times,serif": 'Times New Roman',
      "Verdana,Geneva,sans-serif": 'Verdana'
    },
    fontFamilySelection: true,
    fontFamilyDefaultSelection: 'Arial',
    tableDefaultBorderStyle: 'none',
    tableDefaultBorderWidth: 0,
  };

  constructor(
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<FroalaEditorComponent>,
    private store: Store,
    private editLanguagesService: EditLanguagesService,
    private froalaEditorService: FroalaEditorService,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    this.validators = data.validators;
    this.content = data.content;
    this.isReplacement = data.isReplacement;
  }

  ngOnInit(): void {
    this.store.select(selectUser).subscribe((user: User) => {
      this.user = {...user};
      this.options.imageUploadParams.cp = this.user.selected_customer.cp_id;
    });
    this.addDropdowns();
    this.createPhishLinkPlugin();
    this.addImageLinkPlugin();
  }

  addDropdowns() {
    FroalaEditor.DefineIcon('alignDropdown', {NAME: 'alignLeft', SVG_KEY: 'alignLeft'});
    FroalaEditor.DefineIcon('dropdownIcon', { NAME: 'magic'});
    FroalaEditor.RegisterCommand('alignDropdown', {
      title: 'Align types',
      type: 'dropdown',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      html: function() {
        return `
          <ul class="fr-dropdown-list" role="presentation">
            <li role="presentation"><span style="display:block;" role="presentation"><a class="fr-command" tabindex="-1" role="option" data-cmd="alignDropdown" data-param1="alignLeft" title="Align Left" aria-selected="false"><svg class="fr-svg" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M3,18h6v-2H3V18z M3,11v2h12v-2H3z M3,6v2h18V6H3z"></path></svg></a></span></li>
            <li role="presentation"><span style="display:block;" role="presentation"><a class="fr-command" tabindex="-1" role="option" data-cmd="alignDropdown" data-param1="alignCenter" title="Align Center" aria-selected="false"><svg class="fr-svg" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M9,18h6v-2H9V18z M6,11v2h12v-2H6z M3,6v2h18V6H3z"></path></svg></a></span></li>
            <li role="presentation"><span style="display:block;" role="presentation"><a class="fr-command" tabindex="-1" role="option" data-cmd="alignDropdown" data-param1="alignRight" title="Align Right" aria-selected="false"><svg class="fr-svg" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M15,18h6v-2h-6V18z M9,11v2h12v-2H9z M3,6v2h18V6H3z"></path></svg></a></span></li>
            <li role="presentation"><span style="display:block;" role="presentation"><a class="fr-command" tabindex="-1" role="option" data-cmd="alignDropdown" data-param1="alignJustify" title="Align Justify" aria-selected="false"><svg class="fr-svg" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M3,18h18v-2H3V18z M3,11v2h18v-2H3z M3,6v2h18V6H3z"></path></svg></a></span></li>
          </ul>
        `;
      },
      callback: function (cmd, val) {
        this.commands.exec(val);
      },
    });
    // dropdown
    FroalaEditor.DefineIcon('custom_tags_dropdown', {NAME: 'cog', SVG_KEY: 'cogs'});
    FroalaEditor.RegisterCommand('custom_tags_dropdown', {
      title: 'Advanced options',
      type: 'dropdown',
      focus: false,
      undo: false,
      refreshAfterCallback: true,
      options: {
        '{{sender_name}}': '{{sender_name}}',
        '{{first_name}}': '{{first_name}}',
        '{{customer_name}}': '{{customer_name}}',
        '{{year}}': '{{year}}',
        '{{date}}': '{{date}}',
      },
      callback: function (cmd, val) {
        this.html.insert(val);
        this.events.focus();
      },
    });
  }

  createPhishLinkPlugin() {
    FroalaEditor.POPUP_TEMPLATES['addPhishLinkPlugin.popup'] = '[_CUSTOM_LAYER_]';
    FroalaEditor.DefineIcon('phishLink', { NAME: 'insertLink', SVG_KEY: 'insertLink'});
    FroalaEditor.RegisterCommand('addPhishLink', {
      title: 'Mark selection as phishing link',
      icon: 'phishLink',
      undo: false,
      focus: true,
      plugin: 'addPhishLinkPlugin',
      callback: function () {
        this.commands.exec('linkInsert');
      },
      refreshOnCallback: function() {
      },
    });

    FroalaEditor.RegisterCommand('linkInsert', {
      title: 'Insert Phish Link',
      undo: true,
      focus: false,
      callback: function () {
        const href = '{{link}}';
        const text = this.selection.text() || 'Click here';
        this.link.insert(href, text);
      }
    });

  }

  onClickSave(): void {
    // Sanitize Froala's default table borders before saving
    const sanitizedContent = FroalaEditorService.sanitizeFroalaTableBorders(this.content);

    const result: any = {action: 'saved', content: sanitizedContent, autotranslate: this.autotranslate};

    // Add locale_ids to the request if autotranslate is enabled
    if (this.autotranslate) {
      result.locale_ids = this.editLanguagesService.getSelectedLocaleIds();
    }

    this.dialogRef.close(result);
  }

  onClickRestore(): void {
    this.dialogRef.close({action: 'restored'});
  }

  private addImageLinkPlugin() {
    FroalaEditor.DefineIcon('imagePhishLink', {NAME: 'insertLink', SVG_KEY: 'insertLink'});
    FroalaEditor.RegisterCommand('imagePhishLink', {
      title: 'Add Phishing Link to Image',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        const editor = this;
        let $img = editor.$el.find('img'); // Select the image element in the editor
        if ($img.length > 0) {
          // Check if the image is already wrapped in an <a> tag
          if (!$img.parent().is('a')) {
            // If not wrapped, wrap it in a phishing link
            $img.wrap(`<a href="{{link}}" target="_blank"></a>`);
          } else {
            alert('This image is already linked.');
          }
        } else {
          alert('No image found in the editor to apply a link!');
        }
      }
    });
  }

  openEditLanguagesDialog(): void {
    const selectedLocaleIds = this.editLanguagesService.getSelectedLocaleIds();
    this.editLanguagesService.openEditLanguagesDialog({ selectedLocaleIds })
      .subscribe(result => {
        if (result && result.selectedLocaleIds) {
          // Store the selected locale IDs for later use when submitting the form
          this.editLanguagesService.setSelectedLocaleIds(result.selectedLocaleIds);
        }
      });
  }
}
